import { FC, useEffect, useRef, useState } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import { TextPlugin } from 'gsap/TextPlugin';
import gsap from 'gsap';
import { cn, gsapWait } from 'lib/utils';
import useAudioStore from 'stores/audioStore';
gsap.registerPlugin(TextPlugin);

export type VoiceOverLine = {
  text: string;
  sound?: string;
};

interface VoiceOverTextProps {
  line: VoiceOverLine;
  className?: string | undefined;
  play?: boolean;
  onAnimComplete?: () => void | undefined;
}

const VoiceOverText: FC<VoiceOverTextProps> = ({
  line,
  className,
  play = true,
  onAnimComplete,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const animTimeline = useRef<GSAPTimeline | null>(null);

  const voiceOver = useAudioPlayer();
  const { masterVolume, addVoiceoverDuration, voiceoverDurations } =
    useAudioStore();
  useEffect(() => voiceOver.setVolume(masterVolume), [masterVolume, voiceOver]);
  const [voiceoverDuration, setVoiceoverDuration] = useState<number>(0);

  useEffect(() => {
    if (!voiceOver || !play) return;
    if (line?.sound) {
      const stored = voiceoverDurations.find((vd) => vd.soundId === line.sound);
      if (stored?.duration) setVoiceoverDuration(stored.duration);
      voiceOver.load(`${process.env.PUBLIC_URL}/sound/${line.sound}`);
    } else animateText();
  }, [play]);

  useEffect(() => {
    if (voiceoverDuration > 0) animateText();
  }, [voiceoverDuration]);

  useEffect(() => {
    if (!voiceOver.duration) return;
    if (voiceOver.duration > 0 && line.sound) {
      addVoiceoverDuration(line.sound, voiceOver.duration);
      setVoiceoverDuration(voiceOver.duration);
    }
  }, [voiceOver.duration]);

  const animateText = () => {
    const duration = line?.sound
      ? voiceoverDuration * 0.98
      : line.text.length / 18;
    animTimeline.current = gsap
      .timeline({
        onComplete: () => {
          voiceOver.cleanup();
          if (onAnimComplete) onAnimComplete!();
        },
      })
      .set(textRef.current, { alpha: 1 })
      .call(() => voiceOver.play())
      .to(textRef.current, {
        duration: duration,
        ease: 'none',
        text: { value: line.text },
      });
    gsapWait(0.8, animTimeline.current);
  };

  useEffect(() => {
    return () => {
      if (animTimeline.current) animTimeline.current.kill();
      voiceOver.cleanup();
    };
  }, []);

  return (
    <div className="block relative">
      <div className={cn('block relative opacity-0', className || '')}>
        {line.text}
      </div>
      <div
        ref={textRef}
        className={cn('block absolute top-0 left-0 opacity-0', className || '')}
      />
    </div>
  );
};

export default VoiceOverText;
